import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import logo from '../logo.png';

const Ticker = () => {
    // Fallback data in case API fails or returns zero
    const FALLBACK_DATA = {
        price: '0.00000420',
        marketCap: '420k',
        burned: '69.00'
    };

    const [data, setData] = useState(() => {
        const saved = localStorage.getItem('titstr_ticker_data');
        return saved ? JSON.parse(saved) : FALLBACK_DATA;
    });

    useEffect(() => {
        const fetchData = async () => {
            try {
                // Fetch Price and Market Cap from DexScreener
                const dexResponse = await fetch('https://api.dexscreener.com/latest/dex/pairs/ethereum/0x5e2ef48b2bbfe931223a9c84e4ef4e6224dfec2d9087468c492f9a2529cf8598');
                const dexData = await dexResponse.json();
                const pair = dexData.pair;

                // Fetch Burned Amount from Etherscan V2
                // Burn address: 0x000000000000000000000000000000000000dEaD
                // Contract: 0x6b6994b20be17f193d67886592b3dad5df594060
                const etherscanApiKey = process.env.REACT_APP_ETHERSCAN_API_KEY;
                // Using the standard 'account' module which should still work, but ensuring parameters are correct.
                // The error message suggested V2 migration, but often standard calls work if formatted correctly or using the correct domain.
                // Let's try the standard endpoint again but double check the URL construction.
                // Actually, the error `You are using a deprecated V1 endpoint` suggests we might be hitting an old default.
                // Let's try explicitly using `https://api.etherscan.io/api` which is what we had.
                // Wait, the error message says "switch to API V2 using https://docs.etherscan.io/v2-migration".
                // It seems the issue might be related to how we are calling it or the specific key's tier.
                // However, for a simple token balance, `module=account&action=tokenbalance` is the standard way.
                // Let's try to use the specific V2 URL structure if available, or just ensure we aren't hitting a legacy path.
                // A common issue is not passing the API key correctly or using a blocked IP.
                // But the log said "NOTOK" and "deprecated V1 endpoint".

                // Let's try the chain-specific V2 endpoint pattern if applicable, or just retry with a cleaner URL.
                // Actually, let's try to use the `https://api.etherscan.io/v2/api` if that's what it wants, OR
                // more likely, we can just use the standard one but maybe the `tag=latest` is causing issues or something else.
                // Let's try removing `tag=latest` as it defaults to latest.

                // Alternative: The error might be misleading and it's just a generic error.
                // Let's try to use the `https://api.etherscan.io/api` but with `chainid=1` if it's V2?
                // No, V2 usually involves `https://api.etherscan.io/v2/api?chainid=1...`

                const burnResponse = await fetch(`https://api.etherscan.io/v2/api?chainid=1&module=account&action=tokenbalance&contractaddress=0x6b6994b20be17f193d67886592b3dad5df594060&address=0x000000000000000000000000000000000000dEaD&tag=latest&apikey=${etherscanApiKey}`);
                const burnData = await burnResponse.json();

                // Calculate burned percentage (Total Supply is 1,000,000,000)
                let burnedPercentage = 0;
                if (burnData.result) {
                    const burnedTokens = parseFloat(burnData.result) / 10 ** 18; // Assuming 18 decimals
                    burnedPercentage = (burnedTokens / 1000000000) * 100;
                }

                // Validate DexScreener data
                if (pair && parseFloat(pair.priceUsd) > 0) {
                    // Format Market Cap
                    let formattedMc = '';
                    const fdv = pair.fdv;
                    if (fdv >= 1000000) {
                        formattedMc = (fdv / 1000000).toFixed(2) + 'M';
                    } else if (fdv >= 1000) {
                        formattedMc = (fdv / 1000).toFixed(2) + 'k';
                    } else {
                        formattedMc = fdv.toFixed(2);
                    }

                    const newData = {
                        price: parseFloat(pair.priceUsd).toFixed(8), // Keep 8 decimals for formatting
                        marketCap: formattedMc,
                        burned: burnedPercentage > 0 ? burnedPercentage.toFixed(2) : data.burned // Keep old burn if new is 0
                    };

                    setData(newData);
                    localStorage.setItem('titstr_ticker_data', JSON.stringify(newData));
                } else {
                    console.log('DexScreener returned invalid data (likely no trades), using fallback/cached data.');
                    // On invalid data, we do not update state, thus keeping the last valid data (from init or previous fetch)
                }
            } catch (error) {
                console.error('Error fetching ticker data:', error);
                // On error, we just don't update state, so it keeps the last valid data (from init or previous fetch)
            }
        };

        fetchData();
        const interval = setInterval(fetchData, 60000); // Update every minute
        return () => clearInterval(interval);
    }, []);

    const formatPrice = (price) => {
        if (!price || price === '0.0000') return price;

        const [integerPart, decimalPart] = price.split('.');
        if (!decimalPart) return price;

        let zeroCount = 0;
        for (let char of decimalPart) {
            if (char === '0') zeroCount++;
            else break;
        }

        if (zeroCount === 0) return price;

        const subscriptMap = {
            '0': '₀', '1': '₁', '2': '₂', '3': '₃', '4': '₄',
            '5': '₅', '6': '₆', '7': '₇', '8': '₈', '9': '₉'
        };

        const subscriptCount = zeroCount.toString().split('').map(d => subscriptMap[d]).join('');
        const significantPart = decimalPart.substring(zeroCount);

        // User requested format: 0.0 subscript 4 7540
        // So we return: 0.0 + subscript + significant
        return (
            <span>
                0.0<span className="text-xs">{subscriptCount}</span>{significantPart.substring(0, 4)}
            </span>
        );
    };

    const tickerContent = (
        <div className="flex items-center gap-8 md:gap-12 px-4 md:px-6">
            <span className="text-green-400 font-bold whitespace-nowrap flex items-center">
                PRICE: ${formatPrice(data.price)}
            </span>
            <img src={logo} alt="" className="hidden md:block w-5 h-5 md:w-6 md:h-6 object-contain flex-shrink-0" />
            <span className="text-purple-400 font-bold whitespace-nowrap">MC: ${data.marketCap}</span>
            <img src={logo} alt="" className="hidden md:block w-5 h-5 md:w-6 md:h-6 object-contain flex-shrink-0" />
            <span className="text-pink-500 font-bold whitespace-nowrap">BURNED: {data.burned}%</span>
            <img src={logo} alt="" className="hidden md:block w-5 h-5 md:w-6 md:h-6 object-contain flex-shrink-0" />
        </div>
    );

    return (
        <div className="fixed top-0 w-full z-[60] bg-black border-b border-white/10 h-10 flex items-center overflow-hidden">
            <motion.div
                className="flex whitespace-nowrap"
                animate={{ x: [0, -1000] }}
                transition={{
                    repeat: Infinity,
                    ease: "linear",
                    duration: 20
                }}
            >
                {/* Repeat content multiple times for smooth infinite scroll */}
                {[...Array(10)].map((_, i) => (
                    <React.Fragment key={i}>
                        {tickerContent}
                    </React.Fragment>
                ))}
            </motion.div>
        </div>
    );
};

export default Ticker;
